import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiMenu, FiGrid, FiBook } from 'react-icons/fi';
import styles from './DnsRecord.module.css';
import { URL as BASEURL } from '../AxiosUrlGlobal';

/**
 * DnsRecord - DNS Records Management for Sellers
 * Complete CRUD operations with seller restrictions
 */
function DnsRecord() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [activeTab, setActiveTab] = useState('basic');
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

    const [formData, setFormData] = useState({
        domain: '',
        isActive: true,
        isLocal: true,
        records: {
            A: { enabled: true, ttl: 300, values: [] },
            AAAA: { enabled: false, ttl: 300, values: [] },
            SOA: { enabled: true, ttl: 3600, mname: '', rname: '' },
            NS: { enabled: true, ttl: 3600, values: [] },
            MX: { enabled: false, ttl: 3600, values: [] },
            TXT: { enabled: false, ttl: 300, values: [] },
            SPF: { enabled: false, ttl: 3600, value: '' },
            DKIM: { enabled: false, ttl: 3600, values: [] },
            DMARC: { enabled: false, ttl: 3600, value: '' },
            ACME: { enabled: false, ttl: 300, value: '' },
            CNAME: { enabled: false, ttl: 300, target: '' },
            CAA: { enabled: false, ttl: 3600, values: [] },
            PTR: { enabled: false, ttl: 3600, target: '' },
            SRV: { enabled: false, ttl: 3600, values: [] },
            DNSKEY: { enabled: false, ttl: 86400, flags: 257, protocol: 3, algorithm: 8, publicKey: '' }
        }
    });

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 10,
        hasPrevPage: false,
        hasNextPage: false
    });

    const [filters, setFilters] = useState({
        domain: '',
        isActive: 'all'
    });

    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    useEffect(() => {
        if (userId && token) {
            fetchRecords();
        }
    }, [pagination.currentPage, filters]);

    const fetchRecords = async () => {
        if (!userId || !token) return;

        try {
            setLoading(true);
            setError('');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.isActive !== 'all' && { isActive: filters.isActive }),
                ...(filters.domain && { domain: filters.domain })
            });

            const response = await axios.get(
                `${BASEURL}get-dns/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setRecords(response.data.data.records);
                setPagination(response.data.data.pagination);
            } else {
                setError(response.data.error || 'Failed to load DNS records');
            }
        } catch (err) {
            console.error('Error fetching DNS records:', err);
            setError(err.response?.data?.error || 'Failed to load DNS records');
        } finally {
            setLoading(false);
        }
    };

    const openModal = (record = null) => {
        // Default records structure
        const defaultRecords = {
            A: { enabled: true, ttl: 300, values: [] },
            AAAA: { enabled: false, ttl: 300, values: [] },
            SOA: { enabled: true, ttl: 3600, mname: '', rname: '' },
            NS: { enabled: true, ttl: 3600, values: [] },
            MX: { enabled: false, ttl: 3600, values: [] },
            TXT: { enabled: false, ttl: 300, values: [] },
            SPF: { enabled: false, ttl: 3600, value: '' },
            DKIM: { enabled: false, ttl: 3600, values: [] },
            DMARC: { enabled: false, ttl: 3600, value: '' },
            ACME: { enabled: false, ttl: 300, value: '' },
            CNAME: { enabled: false, ttl: 300, target: '' },
            CAA: { enabled: false, ttl: 3600, values: [] },
            PTR: { enabled: false, ttl: 3600, target: '' },
            SRV: { enabled: false, ttl: 3600, values: [] },
            DNSKEY: { enabled: false, ttl: 86400, flags: 257, protocol: 3, algorithm: 8, publicKey: '' }
        };

        if (record) {
            setEditingRecord(record);
            // Merge backend data with defaults
            const mergedRecords = { ...defaultRecords };
            if (record.records) {
                Object.keys(record.records).forEach(key => {
                    if (mergedRecords[key]) {
                        mergedRecords[key] = { ...mergedRecords[key], ...record.records[key] };
                    }
                });
            }
            setFormData({
                domain: record.domain,
                isActive: record.isActive,
                isLocal: record.isLocal !== undefined ? record.isLocal : true,
                records: mergedRecords
            });
        } else {
            setEditingRecord(null);
            setFormData({
                domain: '',
                isActive: true,
                isLocal: true,
                records: defaultRecords
            });
        }
        setActiveTab('basic');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingRecord(null);
        setActiveTab('basic');
    };

    const handleSubmit = async () => {
        try {
            if (!formData.domain || !formData.domain.trim()) {
                alert('Domain name is required');
                return;
            }

            setSubmitting(true);

            const url = editingRecord
                ? `${BASEURL}edit-dns/${userId}`
                : `${BASEURL}create-dns/${userId}`;

            const payload = editingRecord
                ? { recordId: editingRecord._id, ...formData, uid: userId }
                : { ...formData, uid: userId };

            const response = await axios.post(url, payload, { headers: { token } });

            if (response.data.success) {
                closeModal();
                fetchRecords();
                alert('DNS record saved successfully!');
            } else {
                alert(response.data.error || 'Failed to save DNS record');
            }
        } catch (err) {
            console.error('Error saving DNS record:', err);
            alert(err.response?.data?.error || 'Failed to save DNS record');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (recordId, domain) => {
        if (!window.confirm(`Are you sure you want to delete DNS record for "${domain}"?`)) {
            return;
        }

        try {
            const response = await axios.post(
                `${BASEURL}delete-dns/${userId}`,
                { recordId },
                { headers: { token } }
            );

            if (response.data.success) {
                fetchRecords();
                alert('DNS record deleted successfully');
            } else {
                alert(response.data.error || 'Failed to delete DNS record');
            }
        } catch (err) {
            console.error('Error deleting DNS record:', err);
            alert(err.response?.data?.error || 'Failed to delete DNS record');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const getRecordTypeCounts = (records) => {
        const counts = {};
        if (records.A?.enabled && records.A.values?.length > 0) counts.A = records.A.values.length;
        if (records.AAAA?.enabled && records.AAAA.values?.length > 0) counts.AAAA = records.AAAA.values.length;
        if (records.MX?.enabled && records.MX.values?.length > 0) counts.MX = records.MX.values.length;
        if (records.NS?.enabled && records.NS.values?.length > 0) counts.NS = records.NS.values.length;
        if (records.TXT?.enabled && records.TXT.values?.length > 0) counts.TXT = records.TXT.values.length;
        if (records.CNAME?.enabled) counts.CNAME = 1;
        if (records.SOA?.enabled) counts.SOA = 1;
        return counts;
    };

    // DKIM is stored as values: [{ selector, value }] (one entry edited here), not flat selector/value
    const setDkimField = (field, val) => {
        setFormData(prev => {
            const current = prev.records.DKIM.values?.[0] || { selector: 'default', value: '' };
            const rest = (prev.records.DKIM.values || []).slice(1);
            return {
                ...prev,
                records: { ...prev.records, DKIM: { ...prev.records.DKIM, values: [{ ...current, [field]: val }, ...rest] } }
            };
        });
    };

    // Array field helpers
    const addArrayItem = (recordType, item) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: [...(prev.records[recordType].values || []), item]
                }
            }
        }));
    };

    const removeArrayItem = (recordType, index) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: prev.records[recordType].values.filter((_, i) => i !== index)
                }
            }
        }));
    };

    const updateArrayItem = (recordType, index, field, value) => {
        setFormData(prev => ({
            ...prev,
            records: {
                ...prev.records,
                [recordType]: {
                    ...prev.records[recordType],
                    values: prev.records[recordType].values.map((item, i) =>
                        i === index ? { ...item, [field]: value } : item
                    )
                }
            }
        }));
    };

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>Domain *</label>
                <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    placeholder="example.com"
                    disabled={!!editingRecord}
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    You can only create DNS records for domains you own
                </small>
            </div>

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.isLocal}
                        onChange={(e) => setFormData({ ...formData, isLocal: e.target.checked })}
                        disabled={!!editingRecord}
                    />
                    Local Domain (Platform-managed DNS)
                </label>
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {editingRecord
                        ? 'Cannot change DNS type for existing records'
                        : 'Local domains use our IP pool. Uncheck for external DNS management.'
                    }
                </small>
            </div>
        </div>
    );

    const renderARecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.A.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, A: { ...formData.records.A, enabled: e.target.checked } }
                        })}
                    />
                    Enable A Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.A.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, A: { ...formData.records.A, ttl: parseInt(e.target.value) || 300 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>IPv4 Addresses</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('A', '')}
                >
                    + Add IP
                </button>
            </div>

            {formData.records.A.values.map((ip, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ip}
                        onChange={(e) => {
                            const newValues = [...formData.records.A.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, A: { ...formData.records.A, values: newValues } }
                            });
                        }}
                        placeholder="192.168.1.1"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('A', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderNSRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.NS.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, NS: { ...formData.records.NS, enabled: e.target.checked } }
                        })}
                    />
                    Enable NS Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.NS.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, NS: { ...formData.records.NS, ttl: parseInt(e.target.value) || 3600 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>Name Servers</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('NS', '')}
                >
                    + Add NS
                </button>
            </div>

            {formData.records.NS.values.map((ns, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ns}
                        onChange={(e) => {
                            const newValues = [...formData.records.NS.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, NS: { ...formData.records.NS, values: newValues } }
                            });
                        }}
                        placeholder="ns1.example.com"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('NS', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderMXRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.MX.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, MX: { ...formData.records.MX, enabled: e.target.checked } }
                        })}
                    />
                    Enable MX Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.MX.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, MX: { ...formData.records.MX, ttl: parseInt(e.target.value) || 3600 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>Mail Servers</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('MX', { priority: 10, exchange: '' })}
                >
                    + Add MX
                </button>
            </div>

            {formData.records.MX.values.map((mx, index) => (
                <div key={index} className={styles.arrayItem}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Priority</label>
                            <input
                                type="number"
                                value={mx.priority || 10}
                                onChange={(e) => updateArrayItem('MX', index, 'priority', parseInt(e.target.value) || 10)}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Mail Server</label>
                            <input
                                type="text"
                                value={mx.exchange || ''}
                                onChange={(e) => updateArrayItem('MX', index, 'exchange', e.target.value)}
                                placeholder="mail.example.com"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('MX', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    const renderTXTRecordsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.TXT.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, TXT: { ...formData.records.TXT, enabled: e.target.checked } }
                        })}
                    />
                    Enable TXT Records
                </label>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.TXT.ttl}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, TXT: { ...formData.records.TXT, ttl: parseInt(e.target.value) || 300 } }
                    })}
                />
            </div>

            <div className={styles.arrayHeader}>
                <h3>TXT Records</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('TXT', '')}
                >
                    + Add TXT
                </button>
            </div>

            {formData.records.TXT.values.map((txt, index) => (
                <div key={index} className={styles.arrayItem}>
                    <textarea
                        value={txt}
                        onChange={(e) => {
                            const newValues = [...formData.records.TXT.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, TXT: { ...formData.records.TXT, values: newValues } }
                            });
                        }}
                        placeholder="v=spf1 include:_spf.example.com ~all"
                        rows="3"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('TXT', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* SPF */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.SPF.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SPF: { ...formData.records.SPF, enabled: e.target.checked } }
                        })}
                    />
                    Enable SPF Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>SPF Value</label>
                <input
                    type="text"
                    value={formData.records.SPF.value}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, SPF: { ...formData.records.SPF, value: e.target.value } }
                    })}
                    placeholder="v=spf1 include:_spf.example.com ~all"
                />
            </div>

            {/* DKIM */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.DKIM.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, DKIM: { ...formData.records.DKIM, enabled: e.target.checked } }
                        })}
                    />
                    Enable DKIM Record
                </label>
            </div>
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Selector</label>
                    <input
                        type="text"
                        value={formData.records.DKIM.values?.[0]?.selector ?? 'default'}
                        onChange={(e) => setDkimField('selector', e.target.value)}
                        placeholder="default"
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Public Key</label>
                    <input
                        type="text"
                        value={formData.records.DKIM.values?.[0]?.value ?? ''}
                        onChange={(e) => setDkimField('value', e.target.value)}
                        placeholder="v=DKIM1; k=rsa; p=..."
                    />
                </div>
            </div>

            {/* DMARC */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.DMARC.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, DMARC: { ...formData.records.DMARC, enabled: e.target.checked } }
                        })}
                    />
                    Enable DMARC Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>DMARC Value</label>
                <input
                    type="text"
                    value={formData.records.DMARC.value}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, DMARC: { ...formData.records.DMARC, value: e.target.value } }
                    })}
                    placeholder="v=DMARC1; p=quarantine; rua=mailto:dmarc@example.com"
                />
            </div>
        </div>
    );

    const renderSSLTab = () => (
        <div className={styles.tabContent}>
            {/* ACME Challenge for SSL Certificate */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.ACME?.enabled || false}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: {
                                ...formData.records,
                                ACME: {
                                    ...formData.records.ACME,
                                    enabled: e.target.checked
                                }
                            }
                        })}
                    />
                    Enable ACME Challenge (SSL Certificate)
                </label>
                <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    Auto-responds to _acme-challenge.{'{domain}'} DNS queries for SSL certificate validation (Let's Encrypt, certbot, acme.sh)
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>ACME Challenge Token</label>
                <input
                    type="text"
                    value={formData.records.ACME?.value || ''}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: {
                            ...formData.records,
                            ACME: {
                                ...formData.records.ACME,
                                value: e.target.value
                            }
                        }
                    })}
                    placeholder="qaLGOuRin9gsjmRcUVqmGe9vt7bsuzG_GWdro71VPf0"
                    disabled={!formData.records.ACME?.enabled}
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Paste the challenge token provided by certbot or your SSL certificate provider
                </p>
            </div>

            <div className={styles.formGroup}>
                <label>TTL (seconds)</label>
                <input
                    type="number"
                    value={formData.records.ACME?.ttl || 300}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: {
                            ...formData.records,
                            ACME: {
                                ...formData.records.ACME,
                                ttl: parseInt(e.target.value) || 300
                            }
                        }
                    })}
                    disabled={!formData.records.ACME?.enabled}
                />
                <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
                    Recommended: 300 seconds (5 minutes) for faster validation
                </p>
            </div>

            {/* Example Usage */}
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '15px',
                borderRadius: '5px',
                marginTop: '20px',
                border: '1px solid #dee2e6'
            }}>
                <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#495057' }}>
                    <FiBook style={{ verticalAlign: 'middle' }} /> How to Use ACME Challenge for SSL
                </h4>
                <ol style={{ marginBottom: 0, paddingLeft: '20px', color: '#6c757d', fontSize: '0.9em' }}>
                    <li>Run <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>certbot certonly --manual --preferred-challenges dns -d {formData.domain || 'yourdomain.com'}</code></li>
                    <li>Certbot will provide you an ACME challenge token</li>
                    <li>Enable ACME Challenge above and paste the token</li>
                    <li>Save this DNS record</li>
                    <li>Wait 1-2 minutes for DNS propagation</li>
                    <li>Press Enter in certbot to validate</li>
                    <li>Your SSL certificate will be issued!</li>
                </ol>
                <p style={{ marginTop: '10px', marginBottom: 0, fontSize: '0.85em', color: '#6c757d' }}>
                    <strong>Note:</strong> The ACME challenge automatically responds to queries for <code style={{ background: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>_acme-challenge.{formData.domain || 'yourdomain.com'}</code>
                </p>
            </div>
        </div>
    );

    const renderOtherRecordsTab = () => (
        <div className={styles.tabContent}>
            {/* CNAME */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.CNAME.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, CNAME: { ...formData.records.CNAME, enabled: e.target.checked } }
                        })}
                    />
                    Enable CNAME Record
                </label>
            </div>
            <div className={styles.formGroup}>
                <label>CNAME Target</label>
                <input
                    type="text"
                    value={formData.records.CNAME.target}
                    onChange={(e) => setFormData({
                        ...formData,
                        records: { ...formData.records, CNAME: { ...formData.records.CNAME, target: e.target.value } }
                    })}
                    placeholder="target.example.com"
                />
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* SOA */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.SOA.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, enabled: e.target.checked } }
                        })}
                    />
                    Enable SOA Record
                </label>
            </div>
            <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>MNAME (Primary NS)</label>
                    <input
                        type="text"
                        value={formData.records.SOA.mname}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, mname: e.target.value } }
                        })}
                        placeholder="ns1.example.com"
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>RNAME (Admin Email)</label>
                    <input
                        type="text"
                        value={formData.records.SOA.rname}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, SOA: { ...formData.records.SOA, rname: e.target.value } }
                        })}
                        placeholder="admin.example.com"
                    />
                </div>
            </div>

            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid #e1e3e5' }} />

            {/* AAAA */}
            <div className={styles.formGroup}>
                <label>
                    <input
                        type="checkbox"
                        checked={formData.records.AAAA.enabled}
                        onChange={(e) => setFormData({
                            ...formData,
                            records: { ...formData.records, AAAA: { ...formData.records.AAAA, enabled: e.target.checked } }
                        })}
                    />
                    Enable AAAA Records (IPv6)
                </label>
            </div>

            <div className={styles.arrayHeader}>
                <h3>IPv6 Addresses</h3>
                <button
                    type="button"
                    className={styles.addArrayButton}
                    onClick={() => addArrayItem('AAAA', '')}
                >
                    + Add IPv6
                </button>
            </div>

            {formData.records.AAAA.values.map((ip, index) => (
                <div key={index} className={styles.arrayItem}>
                    <input
                        type="text"
                        value={ip}
                        onChange={(e) => {
                            const newValues = [...formData.records.AAAA.values];
                            newValues[index] = e.target.value;
                            setFormData({
                                ...formData,
                                records: { ...formData.records, AAAA: { ...formData.records.AAAA, values: newValues } }
                            });
                        }}
                        placeholder="2001:0db8:85a3:0000:0000:8a2e:0370:7334"
                    />
                    <button
                        type="button"
                        className={styles.removeArrayButton}
                        onClick={() => removeArrayItem('AAAA', index)}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div>
                    <h1>DNS Records Management</h1>
                    <p>View and manage DNS records for your domains</p>
                </div>
                <div className={styles.headerActions}>
                    <div className={styles.viewToggle}>
                        <button
                            className={`${styles.viewButton} ${viewMode === 'list' ? styles.activeView : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List View"
                        >
                            <FiMenu />
                        </button>
                        <button
                            className={`${styles.viewButton} ${viewMode === 'grid' ? styles.activeView : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                        >
                            <FiGrid />
                        </button>
                    </div>
                    <button className={styles.addButton} onClick={() => openModal()}>
                        + Add DNS Record
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search by domain..."
                    value={filters.domain}
                    onChange={(e) => handleFilterChange('domain', e.target.value)}
                    className={styles.searchInput}
                />
                <select
                    value={filters.isActive}
                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="all">All Status</option>
                    <option value="true">Active Only</option>
                    <option value="false">Inactive Only</option>
                </select>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {loading ? (
                <div style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:'12px',overflow:'hidden'}}>
                    <table style={{width:'100%',borderCollapse:'collapse'}}>
                        <thead style={{background:'#f6f6f7',borderBottom:'2px solid #e1e3e5'}}>
                            <tr>
                                {['Domain','Type','Status','Created','Actions'].map(col => (
                                    <th key={col} style={{padding:'12px 16px',textAlign:'left',fontSize:'13px',fontWeight:'600',color:'#202223',textTransform:'uppercase',letterSpacing:'0.5px'}}>{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[1,2,3,4,5].map(row => (
                                <tr key={row} style={{borderBottom:'1px solid #e1e3e5'}}>
                                    <td style={{padding:'16px'}}><div className={styles.skeleton} style={{width:'180px',height:'18px'}}></div></td>
                                    <td style={{padding:'16px'}}><div className={styles.skeleton} style={{width:'100px',height:'24px',borderRadius:'4px'}}></div></td>
                                    <td style={{padding:'16px'}}><div className={styles.skeleton} style={{width:'70px',height:'24px',borderRadius:'4px'}}></div></td>
                                    <td style={{padding:'16px'}}><div className={styles.skeleton} style={{width:'90px',height:'18px'}}></div></td>
                                    <td style={{padding:'16px'}}>
                                        <div style={{display:'flex',gap:'8px'}}>
                                            <div className={styles.skeleton} style={{width:'60px',height:'32px',borderRadius:'6px'}}></div>
                                            <div className={styles.skeleton} style={{width:'65px',height:'32px',borderRadius:'6px'}}></div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : records.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No DNS records found</p>
                    <p className={styles.emptyHint}>Click "Add DNS Record" to create your first record.</p>
                    <button onClick={() => openModal()}>Create First Record</button>
                </div>
            ) : (
                <>
                    {/* List View (Table) */}
                    {viewMode === 'list' && (
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Domain</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.map((record) => {
                                        return (
                                            <tr key={record._id}>
                                                <td>
                                                    <strong>{record.domain}</strong>
                                                </td>
                                                <td>
                                                    <span className={styles.typeBadge}>
                                                        {record.isLocal ? (record.createdBy === 'system' ? 'Local (System)' : 'Local (User)') : 'External (User)'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={record.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                        {record.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{new Date(record.createdAt).toLocaleDateString()}</td>
                                                <td>
                                                    <div className={styles.tableActions}>
                                                        <button
                                                            className={styles.editButton}
                                                            onClick={() => openModal(record)}
                                                            title="Edit DNS record"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className={styles.deleteButton}
                                                            onClick={() => handleDelete(record._id, record.domain)}
                                                            disabled={record.createdBy === 'system'}
                                                            title={record.createdBy === 'system' ? "Cannot delete system-created DNS records" : "Delete DNS record"}
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Grid View (Cards) */}
                    {viewMode === 'grid' && (
                        <div className={styles.configGrid}>
                            {records.map((record) => {
                                const recordCounts = getRecordTypeCounts(record.records);
                                return (
                                    <div key={record._id} className={styles.configCard}>
                                        <div className={styles.configHeader}>
                                            <div>
                                                <h3>{record.domain}</h3>
                                                <p className={styles.domainMeta}>
                                                    {record.isLocal ? (record.createdBy === 'system' ? 'Local Domain (System)' : 'Local Domain (User)') : 'External Domain (User)'}
                                                </p>
                                            </div>
                                            <span className={record.isActive ? styles.activeBadge : styles.inactiveBadge}>
                                                {record.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>

                                        <div className={styles.configDetails}>
                                            <div className={styles.detailSection}>
                                                <strong>DNS Records Overview</strong>
                                                <div className={styles.recordBadges}>
                                                    {Object.entries(recordCounts).map(([type, count]) => (
                                                        <span key={type} className={styles.recordBadge}>
                                                            {type} ({count})
                                                        </span>
                                                    ))}
                                                    {Object.keys(recordCounts).length === 0 && (
                                                        <span className={styles.noRecords}>No records configured</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className={styles.detailRow}>
                                                <strong>Created:</strong>
                                                <span>{new Date(record.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <div className={styles.detailRow}>
                                                <strong>Last Updated:</strong>
                                                <span>{new Date(record.updatedAt).toLocaleDateString()}</span>
                                            </div>
                                        </div>

                                        <div className={styles.configActions}>
                                            <button
                                                className={styles.editButton}
                                                onClick={() => openModal(record)}
                                                title="Edit DNS record"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className={styles.deleteButton}
                                                onClick={() => handleDelete(record._id, record.domain)}
                                                disabled={record.createdBy === 'system'}
                                                title={record.createdBy === 'system' ? "Cannot delete system-created DNS records" : "Delete DNS record"}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={!pagination.hasPrevPage}
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                            >
                                Previous
                            </button>
                            <span>
                                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} records)
                            </span>
                            <button
                                disabled={!pagination.hasNextPage}
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && closeModal()}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>{editingRecord ? 'Edit DNS Record' : 'Create DNS Record'}</h2>
                            <button className={styles.closeButton} onClick={closeModal}>×</button>
                        </div>

                        <div className={styles.tabs}>
                            <button
                                className={activeTab === 'basic' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('basic')}
                            >
                                Basic Info
                            </button>
                            <button
                                className={activeTab === 'a' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('a')}
                            >
                                A Records
                            </button>
                            <button
                                className={activeTab === 'ns' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('ns')}
                            >
                                NS Records
                            </button>
                            <button
                                className={activeTab === 'mx' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('mx')}
                            >
                                MX Records
                            </button>
                            <button
                                className={activeTab === 'txt' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('txt')}
                            >
                                TXT / Email
                            </button>
                            <button
                                className={activeTab === 'ssl' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('ssl')}
                            >
                                SSL
                            </button>
                            <button
                                className={activeTab === 'other' ? styles.activeTab : ''}
                                onClick={() => setActiveTab('other')}
                            >
                                Other
                            </button>
                        </div>

                        <form style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                            {activeTab === 'basic' && renderBasicTab()}
                            {activeTab === 'a' && renderARecordsTab()}
                            {activeTab === 'ns' && renderNSRecordsTab()}
                            {activeTab === 'mx' && renderMXRecordsTab()}
                            {activeTab === 'txt' && renderTXTRecordsTab()}
                            {activeTab === 'ssl' && renderSSLTab()}
                            {activeTab === 'other' && renderOtherRecordsTab()}
                        </form>

                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={closeModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.submitButton}
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : (editingRecord ? 'Update Record' : 'Create Record')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DnsRecord;
